/**
 * WAV в памяти: PCM 16 бит, моно. Нужен мок-режиму (плееру нужен настоящий файл с
 * длительностью, иначе не проверить перемотку, таймер сна и автопереход) и разблокировке
 * звука на iPhone (короткая тишина в жесте пользователя).
 */

export interface ToneOptions {
  seconds: number;
  sampleRate?: number;
  /** Основной тон, Гц. Рядом тише звучит квинта — мягкий гул, а не писк. */
  frequency?: number;
  /** 0..1 от полной шкалы. Тон «тихий»: 0.05 — фон, который не раздражает. */
  volume?: number;
}

const HEADER_BYTES = 44;

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
}

export function toneWav(options: ToneOptions): ArrayBuffer {
  const sampleRate = options.sampleRate ?? 16_000;
  const frequency = options.frequency ?? 220;
  const volume = Math.min(1, Math.max(0, options.volume ?? 0.05));
  const samples = Math.max(0, Math.round(options.seconds * sampleRate));
  const dataBytes = samples * 2;
  const buffer = new ArrayBuffer(HEADER_BYTES + dataBytes);
  const view = new DataView(buffer);

  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // размер блока fmt
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // моно
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // байт в секунду
  view.setUint16(32, 2, true); // байт на отсчёт
  view.setUint16(34, 16, true); // бит на отсчёт
  writeAscii(view, 36, "data");
  view.setUint32(40, dataBytes, true);

  if (volume === 0) return buffer; // тишина: нули уже на месте

  // Плавные края: щелчок в начале и в конце слышен даже на тихом тоне
  const edge = Math.max(1, Math.min(samples / 2, sampleRate * 0.4));
  const tau = 2 * Math.PI;
  for (let i = 0; i < samples; i += 1) {
    const time = i / sampleRate;
    const tone = 0.7 * Math.sin(tau * frequency * time) + 0.3 * Math.sin(tau * frequency * 1.5 * time);
    // Медленное «дыхание» громкости: так на слух понятно, что звук идёт
    const breath = 0.75 + 0.25 * Math.sin(tau * 0.2 * time);
    const envelope = Math.min(1, i / edge, (samples - 1 - i) / edge);
    view.setInt16(HEADER_BYTES + i * 2, Math.round(tone * breath * envelope * volume * 32_767), true);
  }
  return buffer;
}

/** Адрес для <audio src>: blob живёт, пока открыта вкладка. */
export function wavObjectUrl(buffer: ArrayBuffer): string {
  return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}
